import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { saveCarousel, loadCarousel } from '../services/editorService';
import { getMediaUrl } from '../utils/mediaUrl';
import { renderSlide } from '../services/api';

const EditorContext = createContext();

// Platform presets for canvas dimensions
export const PLATFORM_PRESETS = {
  instagram_square: { name: 'Instagram Square', width: 1080, height: 1080 },
  instagram_story:  { name: 'Instagram Story',  width: 1080, height: 1920 },
  instagram_feed:   { name: 'Instagram Feed',   width: 1080, height: 1350 },
  linkedin_post:    { name: 'LinkedIn Post',     width: 1080, height: 1350 },
  linkedin_square:  { name: 'LinkedIn Square',   width: 1080, height: 1080 },
  facebook_post:    { name: 'Facebook Post',     width: 1080, height: 1080 },
  twitter_post:     { name: 'Twitter Post',      width: 1200, height: 675  },
  custom:           { name: 'Custom',            width: 1080, height: 1080 },
};

export const EditorProvider = ({ children }) => {
  // Canvas state
  const [canvas, setCanvas] = useState(null);
  const stageRef = useRef(null);
  
  // Slides state
  const [slides, setSlides] = useState([
    { 
      id: 1, 
      name: 'Slide 1', 
      elements: [],
      backgroundColor: '#FFFFFF',
    }
  ]);
  const [activeSlide, setActiveSlide] = useState(0);
  
  // Selection state
  const [selectedElements, setSelectedElements] = useState([]);
  
  // Tool state
  const [activeTool, setActiveTool] = useState('select');
  
  // Canvas dimensions (default to LinkedIn size)
  const [dimensions, setDimensions] = useState({
    width: 1080,
    height: 1080
  });
  
  // Canvas zoom (visual only — does not affect element coordinates or export)
  const [zoom, setZoomRaw] = useState(0.7);
  const adjustZoom = useCallback((newZoom) => {
    setZoomRaw(Math.min(2.0, Math.max(0.25, parseFloat(newZoom.toFixed(2)))));
  }, []);

  // History state (for undo/redo)
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState(null);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  
  // Carousel data (from backend)
  const [carouselData, setCarouselData] = useState(null);
  const [carouselId, setCarouselId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // Raw backend slides — preserves backend IDs needed for PATCH on save
  const [backendSlides, setBackendSlides] = useState([]);
  // Clipboard for copy/paste (persists across slide switches)
  const [clipboard, setClipboard] = useState([]);

  // Dirty slide tracking — set of frontend slide indices that have changed
  // since their last render. Auto-save only renders dirty slides; manual
  // save renders all. Using a ref so mutations don't trigger re-renders.
  const dirtySlideIndices = useRef(new Set());
  
  // Add element to current slide
  const addElement = useCallback((element) => {
    const newSlides = [...slides];
    newSlides[activeSlide].elements.push(element);
    setSlides(newSlides);
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update element properties
  const updateElement = useCallback((id, updates) => {
    const newSlides = [...slides];
    const elementIndex = newSlides[activeSlide].elements.findIndex(
      el => el.id === id
    );
    
    if (elementIndex !== -1) {
      newSlides[activeSlide].elements[elementIndex] = {
        ...newSlides[activeSlide].elements[elementIndex],
        ...updates
      };
      setSlides(newSlides);
      dirtySlideIndices.current.add(activeSlide);
      saveToHistory(newSlides);
    }
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Delete element
  const deleteElement = useCallback((id) => {
    const newSlides = [...slides];
    newSlides[activeSlide].elements = newSlides[activeSlide].elements.filter(
      el => el.id !== id
    );
    setSlides(newSlides);
    setSelectedElements([]);
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Delete selected elements
  const deleteSelected = useCallback(() => {
    if (selectedElements.length === 0) return;
    
    const newSlides = [...slides];
    newSlides[activeSlide].elements = newSlides[activeSlide].elements.filter(
      el => !selectedElements.includes(el.id)
    );
    setSlides(newSlides);
    setSelectedElements([]);
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide, selectedElements]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Copy selected elements to clipboard
  const copyElements = useCallback(() => {
    if (selectedElements.length === 0) return;
    const currentElements = slides[activeSlide].elements;
    const copied = selectedElements
      .map(id => currentElements.find(el => el.id === id))
      .filter(Boolean)
      .map(el => ({ ...el }));
    setClipboard(copied);
  }, [slides, activeSlide, selectedElements]);

  // Paste clipboard elements onto active slide (+20px offset, new IDs)
  const pasteElements = useCallback(() => {
    if (clipboard.length === 0) return;
    const OFFSET = 20;
    const newSlides = [...slides];
    const pasted = clipboard.map(el => ({
      ...el,
      id: `${el.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      x: (el.x || 0) + OFFSET,
      y: (el.y || 0) + OFFSET,
    }));
    newSlides[activeSlide].elements = [...newSlides[activeSlide].elements, ...pasted];
    setSlides(newSlides);
    setSelectedElements(pasted.map(el => el.id));
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide, clipboard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle lock on a single element (locked = no drag/resize/select on canvas)
  const toggleLock = useCallback((id) => {
    const newSlides = [...slides];
    const elementIndex = newSlides[activeSlide].elements.findIndex(el => el.id === id);
    if (elementIndex !== -1) {
      const el = newSlides[activeSlide].elements[elementIndex];
      const willBeLocked = !el.locked;
      newSlides[activeSlide].elements[elementIndex] = { ...el, locked: willBeLocked };
      setSlides(newSlides);
      if (willBeLocked) {
        setSelectedElements(prev => prev.filter(selId => selId !== id));
      }
      dirtySlideIndices.current.add(activeSlide);
      saveToHistory(newSlides);
    }
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Duplicate selected elements (offset +20px, select new copies)
  const duplicateElement = useCallback(() => {
    if (selectedElements.length === 0) return;
    const OFFSET = 20;
    const newSlides = [...slides];
    const currentElements = newSlides[activeSlide].elements;
    const duplicates = selectedElements
      .map(id => currentElements.find(el => el.id === id))
      .filter(Boolean)
      .map(el => ({
        ...el,
        id: `${el.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        x: (el.x || 0) + OFFSET,
        y: (el.y || 0) + OFFSET,
      }));
    newSlides[activeSlide].elements = [...currentElements, ...duplicates];
    setSlides(newSlides);
    setSelectedElements(duplicates.map(el => el.id));
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide, selectedElements]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save to history (for undo/redo)
  const saveToHistory = useCallback((newSlides) => {
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(JSON.stringify(newSlides));
    
    // Limit history to 50 steps
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryStep(historyStep + 1);
    }
    
    setHistory(newHistory);
  }, [history, historyStep]);
  
  // Undo
  const undo = useCallback(() => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      const restored = JSON.parse(history[newStep]);
      setSlides(restored);
      // Mark all slides dirty — undo can affect any slide
      restored.forEach((_, i) => dirtySlideIndices.current.add(i));
    }
  }, [history, historyStep]);
  
  // Redo
  const redo = useCallback(() => {
    if (historyStep < history.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      const restored = JSON.parse(history[newStep]);
      setSlides(restored);
      // Mark all slides dirty — redo can affect any slide
      restored.forEach((_, i) => dirtySlideIndices.current.add(i));
    }
  }, [history, historyStep]);
  
  // Reorder slides via drag-and-drop
  const reorderSlides = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newSlides = [...slides];
    const [moved] = newSlides.splice(fromIndex, 1);
    newSlides.splice(toIndex, 0, moved);
    setSlides(newSlides);
    // Keep activeSlide pointing at the same slide after reorder
    if (activeSlide === fromIndex) {
      setActiveSlide(toIndex);
    } else if (activeSlide > fromIndex && activeSlide <= toIndex) {
      setActiveSlide(activeSlide - 1);
    } else if (activeSlide < fromIndex && activeSlide >= toIndex) {
      setActiveSlide(activeSlide + 1);
    }
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear all elements from the active slide
  const clearSlide = useCallback(() => {
    const newSlides = [...slides];
    newSlides[activeSlide] = { ...newSlides[activeSlide], elements: [] };
    setSlides(newSlides);
    setSelectedElements([]);
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set background color for the active slide
  const setSlideBackground = useCallback((color) => {
    const newSlides = [...slides];
    newSlides[activeSlide] = { ...newSlides[activeSlide], backgroundColor: color };
    setSlides(newSlides);
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add slide
  const addSlide = useCallback(() => {
    const newSlide = {
      id: slides.length + 1,
      name: `Slide ${slides.length + 1}`,
      elements: [],
      backgroundColor: '#FFFFFF',
    };
    const newSlides = [...slides, newSlide];
    setSlides(newSlides);
    setActiveSlide(newSlides.length - 1);
    saveToHistory(newSlides);
  }, [slides]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Delete slide
  const deleteSlide = useCallback((index) => {
    if (slides.length <= 1) return; // Don't delete last slide
    
    const newSlides = slides.filter((_, i) => i !== index);
    setSlides(newSlides);
    
    // Adjust active slide if needed
    if (activeSlide >= newSlides.length) {
      setActiveSlide(newSlides.length - 1);
    }
    
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Duplicate slide
  const duplicateSlide = useCallback((index) => {
    const slideToDuplicate = slides[index];
    const newSlide = {
      ...slideToDuplicate,
      id: slides.length + 1,
      name: `${slideToDuplicate.name} (Copy)`,
      elements: slideToDuplicate.elements.map(el => ({
        ...el,
        id: `${el.id}-copy-${Date.now()}`
      }))
    };
    
    const newSlides = [...slides];
    newSlides.splice(index + 1, 0, newSlide);
    setSlides(newSlides);
    setActiveSlide(index + 1);
    dirtySlideIndices.current.add(index + 1);
    saveToHistory(newSlides);
  }, [slides]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Apply a template to the active slide (replaces its elements)
  const applyTemplate = useCallback((template) => {
    const newSlides = [...slides];
    const newElements = template.elements.map((el) => ({
      ...el,
      id: `${el.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    }));
    newSlides[activeSlide] = { ...newSlides[activeSlide], elements: newElements };
    setSlides(newSlides);
    setSelectedElements([]);
    dirtySlideIndices.current.add(activeSlide);
    saveToHistory(newSlides);
  }, [slides, activeSlide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load carousel from backend (Option A: AI-generated carousel with slides)
  const loadFromBackend = useCallback(async (id) => {
    try {
      setLoading(true);
      setSaveError(null);

      const data = await loadCarousel(id);

      // Always use actual carousel dimensions from backend
      const canvasWidth  = data.width  || 1080;
      const canvasHeight = data.height || 1080;
      setDimensions({ width: canvasWidth, height: canvasHeight });

      // Store raw backend slides for saving (need their .id and .order)
      setBackendSlides(data.slides || []);

      if (data.slides && data.slides.length > 0) {
        const editorSlides = data.slides.map((slide, index) => {
          // If slide was previously edited in the editor, restore saved elements
          if (slide.slide_data?.elements?.length > 0) {
            console.log(`  Slide ${index + 1}: restoring ${slide.slide_data.elements.length} saved element(s)`);
            return {
              id: index + 1,
              name: slide.slide_data.name || slide.title || `Slide ${index + 1}`,
              elements: slide.slide_data.elements,
            };
          }

          // First time in editor — build default elements from AI content
          console.log(`  Slide ${index + 1}: building from AI content`);
          const elements = [];

          // Background image (Flux-generated, without PIL text overlay)
          const bgUrl = slide.base_image_url || slide.image_url;
          if (bgUrl) {
            elements.push({
              id: `bg-${slide.id || index}`,
              type: 'image',
              src: getMediaUrl(bgUrl),
              x: 0,
              y: 0,
              width: canvasWidth,
              height: canvasHeight,
              isBackground: true,
              draggable: false,
            });
          }

          // Header text from AI-generated title
          if (slide.title) {
            elements.push({
              id: `title-${slide.id || index}`,
              type: 'text',
              text: slide.title,
              x: 60,
              y: Math.round(canvasHeight * 0.15),
              width: canvasWidth - 120,
              fontSize: Math.round(canvasWidth * 0.048),
              fontFamily: 'Arial',
              fontStyle: 'bold',
              fill: '#FFFFFF',
              align: 'center',
            });
          }

          // Body text from AI-generated description
          if (slide.description) {
            elements.push({
              id: `desc-${slide.id || index}`,
              type: 'text',
              text: slide.description,
              x: 60,
              y: Math.round(canvasHeight * 0.55),
              width: canvasWidth - 120,
              fontSize: Math.round(canvasWidth * 0.026),
              fontFamily: 'Arial',
              fill: '#FFFFFF',
              align: 'center',
            });
          }

          return {
            id: index + 1,
            name: slide.title || `Slide ${index + 1}`,
            elements,
          };
        });

        setSlides(editorSlides);
        console.log(`✅ Loaded ${editorSlides.length} slide(s) into editor`);
      } else {
        // No slides yet — blank canvas
        console.log('ℹ️ No slides found, starting with blank canvas');
        setSlides([{ id: 1, name: 'Slide 1', elements: [] }]);
      }

      setCarouselData(data);
      setCarouselId(id);
      setLoading(false);
    } catch (error) {
      console.error('❌ Load failed:', error);
      setSaveError(error.message || 'Failed to load carousel');
      setLoading(false);
    }
  }, []);
  
  // Save carousel to backend.
  // renderAll=false (default, used by auto-save): only renders slides that have
  // changed since the last render (tracked via dirtySlideIndices).
  // renderAll=true (used by manual Save button): renders every slide with elements.
  const saveToBackend = useCallback(async (renderAll = false) => {
    if (!carouselId) {
      console.warn('⚠️ No carousel ID - cannot save to backend');
      setSaveError('No carousel ID provided');
      return false;
    }

    try {
      setIsSaving(true);
      setSaveError(null);
      setRenderError(null);

      const canvasData = {
        version: '1.0',
        editor: 'konva',
        slides,
        dimensions,
        background: { color: '#FFFFFF' },
      };

      console.log('💾 Saving carousel data...');
      const updatedCarousel = await saveCarousel(carouselId, canvasData, backendSlides);

      // Refresh backendSlides so subsequent saves have up-to-date IDs
      setBackendSlides(updatedCarousel.slides || []);
      setCarouselData(updatedCarousel);
      setLastSaved(new Date());
      setIsSaving(false);
      console.log('✅ Carousel data saved');

      // Determine which slides to render:
      // - Manual save (renderAll=true): all slides with elements
      // - Auto-save (renderAll=false): only slides that changed since last render
      const backendSlideList = updatedCarousel.slides || [];
      const slidesToRender = backendSlideList.filter((s, index) => {
        if (!s.slide_data?.elements?.length) return false;
        if (renderAll) return true;
        return dirtySlideIndices.current.has(index);
      });

      if (slidesToRender.length > 0) {
        try {
          setIsRendering(true);
          console.log(`🎨 Rendering ${slidesToRender.length} slide(s) (${renderAll ? 'all' : 'dirty only'})...`);
          for (const slide of slidesToRender) {
            const renderResult = await renderSlide(slide.id);
            console.log(`✅ Slide ${slide.id} rendered:`, renderResult.edited_image_url);
          }
          dirtySlideIndices.current.clear();
          setIsRendering(false);
        } catch (renderErr) {
          console.error('❌ Rendering failed:', renderErr);
          setRenderError(renderErr.message || 'Failed to render slide');
          setIsRendering(false);
          // Don't fail the save if rendering fails
        }
      } else {
        console.log('⏭️ No dirty slides to render — skipping render step');
      }
      
      return true;
    } catch (error) {
      console.error('❌ Save failed:', error);
      setSaveError(error.message || 'Failed to save carousel');
      setIsSaving(false);
      return false;
    }
  }, [carouselId, slides, dimensions, backendSlides]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Auto-save every 30 seconds when a carousel is loaded
  const saveToBackendRef = useRef(null);
  useEffect(() => { saveToBackendRef.current = saveToBackend; });
  useEffect(() => {
    if (!carouselId) return;
    const interval = setInterval(() => {
      saveToBackendRef.current?.();
    }, 30000);
    return () => clearInterval(interval);
  }, [carouselId]);

  const value = {
    // State
    canvas,
    stageRef,
    slides,
    activeSlide,
    selectedElements,
    activeTool,
    dimensions,
    isSaving,
    lastSaved,
    isRendering,
    renderError,
    loading,
    carouselData,
    carouselId,
    saveError,
    history,
    historyStep,
    
    // Setters
    setCanvas,
    setSlides,
    setActiveSlide,
    setSelectedElements,
    setActiveTool,
    setDimensions,
    setIsSaving,
    setLastSaved,
    setLoading,
    setCarouselData,
    setCarouselId,
    setSaveError,
    
    // Methods
    addElement,
    updateElement,
    deleteElement,
    deleteSelected,
    duplicateElement,
    copyElements,
    toggleLock,
    setSlideBackground,
    reorderSlides,
    clearSlide,
    pasteElements,
    clipboard,
    undo,
    redo,
    addSlide,
    deleteSlide,
    duplicateSlide,
    
    backendSlides,
    setBackendSlides,

    // Backend methods
    loadFromBackend,
    saveToBackend,

    // Zoom
    zoom,
    adjustZoom,

    // Template methods
    applyTemplate,
  };
  
  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
};

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within EditorProvider');
  }
  return context;
};
